import { AnimatePresence, motion } from "framer-motion";
import React, { useEffect, useRef, useState } from "react";
import { FiChevronDown } from "react-icons/fi";
import { MdOutlineCancel } from "react-icons/md";
import { IconButton } from "./IconButton";
import { Style } from "../../utils/Style";

type Option = {
  label: string;
  value: string;
  data?: Record<string, string | number | boolean>;
};

interface DropdownProps {
  options: Option[];
  value?: string | null | boolean;
  onChange?: (value: Option) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  isDelete?: boolean;
  label?: string;
  required?: boolean;
  error?: string;
  isDownward?: boolean;
  zIndex?: string;
  isSearchable?: boolean;
  buttonClassName?: string;
}

const Dropdown = ({
  options,
  value,
  onChange,
  placeholder = "Select an option",
  className = "",
  disabled = false,
  isDelete = false,
  label,
  required,
  error,
  isDownward = false,
  zIndex = "z-[12]",
  isSearchable = false,
  buttonClassName = "",
}: DropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [openUpward, setOpenUpward] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const selectedOption = options.find((option) => option.value === value);

  const filteredOptions = options.filter((option) =>
    option.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const updatePosition = () => {
    if (!dropdownRef.current || !menuRef.current) return;

    const dropdownRect = dropdownRef.current.getBoundingClientRect();
    const menuHeight = menuRef.current.offsetHeight;
    const windowHeight = window.innerHeight;
    const spaceBelow = windowHeight - dropdownRect.bottom;
    const spaceAbove = dropdownRect.top;

    setOpenUpward(spaceBelow < menuHeight && spaceAbove > spaceBelow);
  };

  useEffect(() => {
    if (isOpen) {
      updatePosition();
      window.addEventListener("scroll", updatePosition);
      window.addEventListener("resize", updatePosition);
      setHighlightedIndex(options.findIndex((opt) => opt.value === value) || 0);

      if (isSearchable && searchInputRef.current) {
        searchInputRef.current.focus();
      }
    } else {
      setSearchTerm("");
    }

    return () => {
      window.removeEventListener("scroll", updatePosition);
      window.removeEventListener("resize", updatePosition);
    };
  }, [isOpen, value, options, isSearchable]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (option: Option) => {
    onChange?.(option);
    setIsOpen(false);
    buttonRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) {
      if (e.key === "Enter" || e.key === " " || e.key === "ArrowDown") {
        e.preventDefault();
        setIsOpen(true);
      }
      return;
    }

    if (e.key === "ArrowDown") {
      e.preventDefault();
      const count =
        highlightedIndex < filteredOptions.length - 1
          ? highlightedIndex + 1
          : filteredOptions.length - 1;
      setHighlightedIndex(count);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      const count = highlightedIndex > 0 ? highlightedIndex - 1 : 0;
      setHighlightedIndex(count);
    } else if (e.key === "Enter" && highlightedIndex >= 0) {
      e.preventDefault();
      if (filteredOptions[highlightedIndex]) {
        handleSelect(filteredOptions[highlightedIndex]);
      }
    } else if (e.key === "Escape") {
      e.preventDefault();
      setIsOpen(false);
      buttonRef.current?.focus();
    } else if (e.key === "Tab") {
      setIsOpen(false);
    }
  };
  useEffect(() => {
    if (isOpen && highlightedIndex >= 0 && menuRef.current) {
      const itemIndex = isSearchable ? highlightedIndex + 1 : highlightedIndex;
      const highlightedElement = menuRef.current.querySelector(
        `li:nth-child(${itemIndex + 1})`
      ) as HTMLElement;

      if (highlightedElement) {
        const searchInputHeight = isSearchable ? 36 : 0;
        const menuContainer = menuRef.current;

        const elementTop = highlightedElement.offsetTop;
        const elementHeight = highlightedElement.offsetHeight;
        const containerHeight = menuContainer.offsetHeight;
        const scrollTop = menuContainer.scrollTop;

        if (elementTop - searchInputHeight < scrollTop) {
          menuContainer.scrollTo({
            top: elementTop - searchInputHeight - 8,
            behavior: "smooth",
          });
        } else if (elementTop + elementHeight > scrollTop + containerHeight) {
          menuContainer.scrollTo({
            top: elementTop - containerHeight + elementHeight + 8,
            behavior: "smooth",
          });
        }
      }
    }
  }, [highlightedIndex, isOpen, isSearchable]);

  return (
    <div className="w-full text-start">
      {label && (
        <label className={Style.label}>
          {label}
          {required && <span className="text-red-500">*</span>}
        </label>
      )}
      <div
        className={"relative w-full text-[12px] 2xl:text-sm" + className}
        ref={dropdownRef}
        onKeyDown={handleKeyDown}
      >
        <button
          type="button"
          ref={buttonRef}
          onClick={() => !disabled && setIsOpen(!isOpen)}
          className={`
            flex w-full items-center justify-between gap-2  text-nowrap rounded border py-2 pl-3 pr-2 text-left focus:border-primary focus:outline-primary focus:ring-1 focus:ring-primary
            ${disabled && "cursor-not-allowed bg-gray-100 text-gray-500"}
            ${error ? "border-red-500" : "border-gray-300"}
            ${buttonClassName}
          `}
          disabled={disabled}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
        >
          {selectedOption ? (
            <div className="flex items-center text-[12px] overflow-hidden">
              {selectedOption.label}
            </div>
          ) : (
            <span className="overflow-hidden text-gray-400 text-[12px]">{placeholder}</span>
          )}
          <div className="flex items-center gap-2">
            {isDelete && value !== "" && (
              <span className="relative">
                <IconButton
                  color="gray"
                  onClick={() => {
                    handleSelect({
                      label: "",
                      value: "",
                    });
                  }}
                  icon={<MdOutlineCancel fontSize={15} />}
                  padding="p-1 absolute -top-[10px] right-0"
                />
              </span>
            )}
            <FiChevronDown
              className={`
                h-4 w-4 transition-transform duration-200
                ${isOpen && "rotate-180 transform"}
              `}
            />
          </div>
        </button>
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
              ref={menuRef}
              className={`
                thin-scrollbar absolute max-h-48 w-full overflow-y-auto rounded-lg border bg-white shadow-lg 2xl:max-h-64
                ${
                  !isDownward
                    ? openUpward
                      ? "bottom-full mb-1"
                      : "top-9 mt-1"
                    : "top-9 mt-1"
                }
                ${zIndex}
              `}
            >
              <ul className={"py-1"} role="listbox">
                {isSearchable && (
                  <li className="sticky top-0 z-10 border-b border-gray-200 bg-white px-2 py-2">
                    <input
                      type="text"
                      ref={searchInputRef}
                      value={searchTerm}
                      onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setHighlightedIndex(0);
                      }}
                      className="w-full rounded-lg border border-gray-300 px-3 py-1.5 text-[12px] focus:outline-primary 2xl:text-sm"
                      placeholder="Search..."
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => {
                        if (
                          e.key === "ArrowDown" &&
                          filteredOptions.length > 0
                        ) {
                          e.preventDefault();
                          setHighlightedIndex(0);
                        }
                      }}
                    />
                  </li>
                )}
                {filteredOptions.length > 0 ? (
                  filteredOptions.map((option, index) => (
                    <li
                      key={index}
                      className={`cursor-pointer px-4 py-2 hover:bg-indigo-50
                        ${
                          option.value === value
                            ? "bg-indigo-100 font-medium text-primary"
                            : "text-gray-900"
                        }
                        ${index === highlightedIndex && "bg-indigo-50"}`}
                      onClick={() => handleSelect(option)}
                      role="option"
                      aria-selected={option.value === value}
                    >
                      <div className="flex items-center gap-2">
                        {option.label}
                      </div>
                    </li>
                  ))
                ) : (
                  <li className="px-4 py-2 text-gray-400">No options found</li>
                )}
              </ul>
            </motion.div>
          )}
        </AnimatePresence>

        {error && (
          <span className="text-red-500 text-[10px] 2xl:text-xs">{error}</span>
        )}
      </div>
    </div>
  );
};

export { Dropdown };
